import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { playDing } from '../utils/playDing';
import { useSocket } from '../context/SocketContext';
import './NotificationCenter.css';

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { onNewNotification } = useSocket() || {};
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevUnreadRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Real-time: push new notification to top of list without waiting for poll
  useEffect(() => {
    if (!onNewNotification) return;
    return onNewNotification((notif) => {
      setNotifications(prev => {
        if (prev.some(n => n.id === notif.id)) return prev;
        return [notif, ...prev].slice(0, 10);
      });
      setUnreadCount(prev => prev + 1);
      playDing();
    });
  }, [onNewNotification]); // eslint-disable-line

  const fetchNotifications = async () => {
    try {
      const { data } = await API.get('/notifications?limit=10');
      const list = data.notifications || [];
      const count = list.filter(n => !n.is_read).length;
      setNotifications(list);
      setUnreadCount(count);
      if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
        playDing();
      }
      prevUnreadRef.current = count;
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = unreadCount > 0 ? `(${unreadCount}) ${base}` : base;
  }, [unreadCount]);

  const markAsRead = async (notificationId) => {
    try {
      await API.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await API.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-center">
      <button
        className="notification-bell"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Notifications"
      >
        🔔
        {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={markAllAsRead}
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowDropdown(false)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'var(--text-soft)', lineHeight:1, padding:'2px 4px', borderRadius:4 }}
                aria-label="Close notifications"
              >✕</button>
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="empty-state">No notifications yet</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => {
                    if (!notification.is_read) markAsRead(notification.id);
                    let d = notification.data || {};
                    if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = {}; } }
                    const dest = notification.link
                      || (d.conversationId ? `/messages/${d.conversationId}` : null)
                      || (notification.type === 'new_message' ? '/messages' : null)
                      || (d.orderId ? `/orders/${d.orderId}` : null)
                      || (d.listingId ? `/listing/${d.listingId}` : null);
                    if (dest) {
                      setShowDropdown(false);
                      if (dest.startsWith('http')) { window.location.href = dest; }
                      else { navigate(dest); }
                    } else { setShowDropdown(false); }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="notification-content">
                    <div className="notification-title" style={{ display:'flex', alignItems:'center', gap:6 }}>
                      {notification.title}
                      {(() => { let d = notification.data || {}; if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = {}; } } return (d.conversationId || d.orderId || d.listingId || notification.type === 'new_message' || notification.link) ? <span style={{ fontSize:9, fontWeight:700, background:'var(--cherry)', color:'white', borderRadius:4, padding:'1px 5px', letterSpacing:'0.5px' }}>TAP</span> : null; })()}
                    </div>
                    <div className="notification-message">{notification.body}</div>
                    {notification.data && (
                      <div className="notification-details">
                        {notification.data.pickupDate && (
                          <div>📅 {notification.data.pickupDate}</div>
                        )}
                        {notification.data.location && (
                          <div>📍 {notification.data.location}</div>
                        )}
                      </div>
                    )}
                    <div className="notification-time">{formatDate(notification.created_at)}</div>
                  </div>
                  {!notification.is_read && <div className="unread-indicator" />}
                </div>
              ))
            )}
          </div>
          <div style={{ borderTop:'1px solid var(--border)', padding:'10px 14px' }}>
            <button
              onClick={() => { setShowDropdown(false); navigate('/dashboard/notifications'); }}
              style={{ width:'100%', background:'none', border:'none', cursor:'pointer', fontSize:13, fontWeight:600, color:'var(--cherry)', textAlign:'center', padding:'4px 0' }}
            >
              View all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
